import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useState
} from 'react';

import { AlertBox, Card, Icon } from '@iwoca/orion';
import classnames from 'classnames';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl
} from 'react-intl';
import { connect } from 'react-redux';

import { useGetSplitTestQuery } from '@lendingAPI/lendingAPIQueries';

import { ACTIVATE_TRADING_HISTORY, ADD_SPLIT_TEST } from './actions';
import BusinessAccountsShareRequirements from './BusinessAccountsShareRequirements/BusinessAccountsShareRequirements';
import withLoadingSpinner from './LoadingContainer/WithLoadingSpinner';
import LoanSummaryMobile from './LoanSummary/LoanSummaryMobile';
import TradingHistoryLoanSummary from './LoanSummary/TradingHistoryLoanSummary';
import useViewport from './LoanSummary/useViewport';
import OptionalStatement from './OptionalStatement/OptionalStatement';
import SubmitCTA from './SubmitCTA/SubmitCTA';
import { useAmplitudetradingHistoryViewed } from './TradingHistory.amplitude';
import {
    getDocumentRequirements,
    getVatDocRequirement,
    RequirementSections
} from './TradingHistory.helpers';
import messages from './TradingHistory.messages';
import styles from './TradingHistory.module.css';
import {
    fetchDocumentRequirements,
    getIsUploadingFileByType,
    getIsOptionalFileUploading
} from './TradingHistory.selectors';
import { TGetOffersResponse, TOffersDataRequirements } from './types';
import VATStatement from './VATStatement/VATStatement';
import Spinner from '../../components/Spinner';
import { userIsIndicativeOfferFlow } from '../../helpers/user';
import {
    getCountryCode,
    getRequestedProductType,
    getSuggestedAmount,
    hasFormalIndicativeOffers
} from '../../lendingAPI/lendingAPISelector';
import { BREAK_S } from '../../matchMedia/breakpoints';
import { AppDispatch, RootState } from '../../reducers';
import { getRequestedAmount, getSplitTestValue } from '../../utils/selectors';
import { isNewOffersEnabled } from '../Offers/offers/offersHelpers';

type TSplitTestExpriment = {
    name: string;
    value: boolean;
};
const mapStateToProps = (state: RootState) => ({
    ready: !state.tradingHistory.loading,
    documentRequirements: fetchDocumentRequirements(state),
    isInIndicativeOfferFlow: userIsIndicativeOfferFlow(state.ui),
    hasFormalIndicativeOffers: hasFormalIndicativeOffers(state),
    isNewOffersEnabled: isNewOffersEnabled(state),
    amount: getSuggestedAmount(state),
    isUploadingFile: (type: string) => getIsUploadingFileByType(state, type),
    isUploadingOptionalFile: getIsOptionalFileUploading(state),
    loanSummary: state?.tradingHistory?.loanSummary,
    requestedProduct: getRequestedProductType(state),
    requestedAmount: getRequestedAmount(state),
    missing: state?.tradingHistory?.missing,
    country: getCountryCode(state),
    selectedOffers: state?.tradingHistory?.selectedOffers,
    isInSplitTestHighProbability: getSplitTestValue(
        state,
        'trading_history_high_probability'
    ),
    isInSplitInterestOnly: getSplitTestValue(
        state,
        'signup_interest_only_test_2023'
    ),
    isBusinessDetailsReductionSplitTest:
        getSplitTestValue(state, 'business_details_reduction') &&
        getCountryCode(state) === 'uk'
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
    activateTradingHistory: () => {
        dispatch({ type: ACTIVATE_TRADING_HISTORY });
    },
    addSplitTest: (experiment: TSplitTestExpriment) => {
        dispatch({
            type: ADD_SPLIT_TEST,
            splitTest: { [experiment.name]: experiment.value }
        });
    }
});
interface Props {
    ready: boolean;
    activateTradingHistory: () => void;
    documentRequirements: TOffersDataRequirements;
    isNewOffersEnabled: boolean;
    isUploadingFile: (type: string) => boolean;
    isUploadingOptionalFile: boolean;
    loanSummary: any;
    requestedProduct: string;
    missing: [];
    country: string;
    selectedOffers: TGetOffersResponse['data']['offers'][number];
    isInSplitTestHighProbability: boolean;
    isInSplitInterestOnly: boolean;
    requestedAmount: number;
    isBusinessDetailsReductionSplitTest: boolean;
    addSplitTest: (experiment: TSplitTestExpriment) => void;
}

const TradingHistory = ({
    activateTradingHistory,
    ready,
    documentRequirements,
    isNewOffersEnabled,
    isUploadingFile,
    isUploadingOptionalFile,
    loanSummary,
    requestedProduct,
    missing,
    country,
    intl,
    selectedOffers,
    isInSplitTestHighProbability,
    isInSplitInterestOnly,
    requestedAmount,
    isBusinessDetailsReductionSplitTest,
    addSplitTest
}: Props & WrappedComponentProps) => {
    const { branch } = useGetSplitTestQuery('remove_vat_requirement_test');
    const [docRequirementVAT, setDocRequirementVAT] = useState<
        TOffersDataRequirements[number]['actions'][number] | null
    >(null);
    const offersAutoEligible = selectedOffers?.is_auto_eligible;

    const isBusinessDetailsReduction =
        isBusinessDetailsReductionSplitTest &&
        branch === 'exclude' &&
        !offersAutoEligible;

    useEffect(() => {
        activateTradingHistory();
    }, [activateTradingHistory]);

    useEffect(() => {
        if (branch === 'include') {
            addSplitTest({
                name: 'remove_vat_requirement_test',
                value: branch === 'include'
            });
        }
    }, [branch, addSplitTest]);

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const probabilityOfOffer = selectedOffers?.probability_of_offer;

    const requiredDocumentRequirements = getDocumentRequirements({
        requirements: documentRequirements,
        isOptional: false,
        isBusinessDetailsReductionSplitTest: isBusinessDetailsReduction
    });

    const optionalDocumentRequirements = getDocumentRequirements({
        requirements: documentRequirements,
        isOptional: true,
        isBusinessDetailsReductionSplitTest: isBusinessDetailsReduction
    });

    useAmplitudetradingHistoryViewed(
        ready,
        documentRequirements,
        requestedProduct,
        probabilityOfOffer,
        requestedAmount,
        parseInt(loanSummary?.amount),
        isBusinessDetailsReduction,
        offersAutoEligible
    );
    const { width } = useViewport();
    const isScreenMobileSize = width <= BREAK_S;

    useEffect(() => {
        if (documentRequirements) {
            setDocRequirementVAT(getVatDocRequirement(documentRequirements));
        }
    }, [documentRequirements]);

    // If there is a data requirement, it will be displayed the respective quantifier value.
    const renderVATSwitch = useCallback(() => {
        const quantifier: number = docRequirementVAT
            ? docRequirementVAT.quantifier.value
            : 0;
        const isOptional: boolean = docRequirementVAT
            ? docRequirementVAT.is_optional
            : true;

        return <VATStatement quantifier={quantifier} isOptional={isOptional} />;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docRequirementVAT]);

    const renderAlertBox = () => {
        return (
            <AlertBox
                variant="failure"
                className={classnames(styles.docAlertBox, {
                    [styles.docAlertBoxBusinessDetailsReduction]:
                        isBusinessDetailsReduction
                })}
                data-testid="document-alert-box">
                <div className={styles.docAlertBoxIconContainer}>
                    <Icon
                        width={20}
                        height={20}
                        icon="exclamationTriangleOutline"
                        className={styles.docAlertBoxIcon}
                    />
                </div>
                <FormattedMessage
                    {...messages.infoRequired}
                    values={{
                        phoneNumber: (
                            <a
                                href={`tel:${intl.formatMessage(
                                    messages.infoRequired_tel
                                )}`}>
                                <FormattedMessage
                                    {...messages.infoRequired_tel_display}
                                />
                            </a>
                        ),

                        email: (
                            <a
                                href={`mailto:${intl.formatMessage(
                                    messages.infoRequired_email
                                )}`}>
                                <FormattedMessage
                                    {...messages.infoRequired_email}
                                />
                            </a>
                        )
                    }}
                />
            </AlertBox>
        );
    };

    return (
        <>
            {(ready || documentRequirements) &&
                (loanSummary.product_type === 'credit_facility' ||
                    loanSummary.product_type === 'flexi24') &&
                !isNewOffersEnabled &&
                country !== 'de' && (
                    <>
                        {!isScreenMobileSize ? (
                            <>
                                <TradingHistoryLoanSummary
                                    amount={loanSummary.amount}
                                    isPartnerCustomer={true}
                                    isInSplitInterestOnly={
                                        isInSplitInterestOnly
                                    }
                                    isInSplitTestHighProbability={
                                        isInSplitTestHighProbability
                                    }
                                    probabilityOfOffer={probabilityOfOffer}
                                    requestedAmount={requestedAmount}
                                    productType={loanSummary.product_type}
                                />
                            </>
                        ) : (
                            <LoanSummaryMobile
                                amount={loanSummary.amount}
                                isInSplitInterestOnly={isInSplitInterestOnly}
                                isPartnerCustomer={true}
                                isInSplitTestHighProbability={
                                    isInSplitTestHighProbability
                                }
                                requestedAmount={requestedAmount}
                                probabilityOfOffer={probabilityOfOffer}
                                productType={loanSummary.product_type}
                            />
                        )}
                    </>
                )}

            <TradingHistoryHeading title={intl.formatMessage(messages.title)} />

            {/* Render alert box outside of the card when in business details reduction split test. */}
            {missing.length > 0 &&
                isBusinessDetailsReduction &&
                renderAlertBox()}

            <Card className={styles.cardContainer}>
                {missing.length > 0 &&
                    !isBusinessDetailsReduction &&
                    renderAlertBox()}
                {ready || documentRequirements ? (
                    <>
                        <div className={styles.requiredSection}>
                            {requiredDocumentRequirements.map(
                                (
                                    requirement: TOffersDataRequirements[number]['actions'][number],
                                    index: number
                                ) => {
                                    if (!requirement.type) return null;

                                    const RequiredComponent: React.ElementType =
                                        RequirementSections[requirement.type];
                                    const isLoadingRequirement =
                                        isUploadingFile(requirement.type);

                                    return (
                                        <div
                                            key={`${requirement.type}-${index}`}>
                                            {withLoadingSpinner(
                                                RequiredComponent,
                                                isLoadingRequirement,
                                                requirement.quantifier.value,
                                                requirement.is_optional
                                            )}
                                        </div>
                                    );
                                }
                            )}
                        </div>
                        {optionalDocumentRequirements.length > 0 &&
                            country === 'de' && (
                                <div
                                    className={styles.optionalSection}
                                    data-testid="optional-section">
                                    <OptionalStatement
                                        isLoading={isUploadingOptionalFile}
                                    />
                                    {optionalDocumentRequirements.map(
                                        (
                                            requirement: TOffersDataRequirements[number]['actions'][number]
                                        ) => {
                                            if (!requirement.type) return null;

                                            const OptionalComponent: React.ElementType =
                                                RequirementSections[
                                                    requirement.type
                                                ];

                                            return (
                                                <div
                                                    key={requirement.type}
                                                    className={
                                                        styles.optionalCard
                                                    }>
                                                    <OptionalComponent
                                                        quantifier={
                                                            requirement
                                                                .quantifier
                                                                .value
                                                        }
                                                        isOptional={
                                                            requirement.is_optional
                                                        }
                                                    />
                                                </div>
                                            );
                                        }
                                    )}
                                </div>
                            )}{' '}
                    </>
                ) : (
                    <div
                        data-testid="documents-page-loader"
                        className={styles.loadingSpinnerContainer}>
                        <Spinner />
                        <TradingHistoryHeading
                            title={intl.formatMessage(
                                messages.preparingRequirements
                            )}
                        />
                    </div>
                )}
            </Card>

            {(ready || documentRequirements) && isBusinessDetailsReduction && (
                <Card
                    className={classnames(
                        styles.cardContainer,
                        styles.vatCardContainer
                    )}>
                    {renderVATSwitch()}
                </Card>
            )}

            {(ready || documentRequirements) && (
                <>
                    <BusinessAccountsShareRequirements
                        locale={country}
                        dataRequirements={documentRequirements}
                    />

                    <SubmitCTA />
                </>
            )}
        </>
    );
};

const TradingHistoryHeading = ({ title }: { title: string }) => {
    return (
        <div className={styles.separator}>
            <h2
                data-testid="trading_history_title"
                className={styles.separatorTitle}>
                {title}
            </h2>
        </div>
    );
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(TradingHistory));
